import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 20000,
});

// Auto-redirect to /login when a request fails auth after the user was
// already signed in. This prevents pages from getting stuck on their
// loading skeletons when the session expires mid-navigation.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    const url = cfg.url || "";
    const method = (cfg.method || "get").toLowerCase();
    const isAuthProbe =
      url.endsWith("/auth/me") ||
      url.endsWith("/auth/login") ||
      url.endsWith("/auth/register");

    // Redirect on real auth failure (skip auth probes to allow the login page
    // to render on first mount, and skip pages that don't need auth).
    if (status === 401 && !isAuthProbe && typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/register") {
        const next = encodeURIComponent(path + window.location.search);
        window.location.replace(`/login?next=${next}`);
        return Promise.reject(err);
      }
    }

    // Transparent retry for transient failures on GET requests:
    //   • network errors (no response at all)
    //   • 502 / 503 / 504 (gateway hiccups)
    //   • request timeouts (ECONNABORTED)
    // Up to 3 retries with quick exponential backoff (250 → 500 → 1000 ms).
    const isTransient =
      method === "get" &&
      !isAuthProbe &&
      (!err.response ||
        [502, 503, 504].includes(status) ||
        err.code === "ECONNABORTED" ||
        err.code === "ERR_NETWORK");

    if (isTransient) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= 3) {
        const delay = 250 * 2 ** (cfg.__retryCount - 1);
        await new Promise((r) => setTimeout(r, delay));
        return api.request(cfg);
      }
    }
    return Promise.reject(err);
  }
);

export function apiErrorMessage(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof d.msg === "string") return d.msg;
  return String(d);
}
